import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { Button, Modal } from "antd";
import { SyncOutlined } from "@ant-design/icons";

import styles from "./serviceWorkerUpdate.module.scss";
import {
  MAX_UPDATE_ATTEMPTS,
  bumpUpdateAttempts,
  getUpdateAttempts,
  resetUpdateAttempts,
} from "./swUpdateAttempts";

const COUNTDOWN_SECONDS = 5;

// Trong lúc chờ SW mới nắm quyền, cứ mỗi khoảng này lại chủ động "đánh thức"
// SW (registration.update + gửi lại SKIP_WAITING). Tab để lâu có thể khiến
// trình duyệt cho SW ngủ, làm message SKIP_WAITING không được xử lý ngay.
const NUDGE_INTERVAL_MS = 5_000;
// Nếu sau ngưỡng này SW vẫn chưa nắm quyền -> coi là "stalled" và chuyển sang
// quy trình recovery leo thang (đánh thức lại -> hard-recovery -> terminal).
const STALL_TIMEOUT_MS = 90_000;
// Sau khi (lại) gửi SKIP_WAITING, chờ tối đa ngần này cho controllerchange. Nếu
// controllerchange xảy ra, serviceWorkerRegistration tự reload trang và code ở
// đây không chạy tiếp; còn sống sau timeout -> SW chưa nắm quyền -> leo thang.
const CONTROLLERCHANGE_WAIT_MS = 4_000;
// Timeout cho probe kiểm tra server có thật sự tới được không.
const REACHABILITY_TIMEOUT_MS = 5_000;

// Mốc kết thúc đếm ngược lưu ở localStorage: DÙNG CHUNG cho mọi tab cùng origin
// và sống sót qua F5. Tab nào hiện modal trước sẽ đặt deadline, các tab sau đọc
// lại đúng mốc đó -> đếm ngược đồng bộ trên tất cả tab.
const DEADLINE_KEY = "app-update-deadline";
// Nếu deadline đã qua lâu hơn ngưỡng này -> coi là của đợt update cũ, tạo đếm
// ngược mới thay vì cập nhật ngay lập tức.
const STALE_GRACE_MS = 15_000;

/**
 * Lấy mốc kết thúc đếm ngược:
 *  - Lần đầu hiển thị: tạo deadline = now + COUNTDOWN_SECONDS và lưu lại.
 *  - Sau khi F5 giữa chừng: đọc lại deadline cũ -> thời gian còn lại được giữ nguyên.
 *  - Nếu deadline đã quá hạn từ lâu (đợt update trước): tạo deadline mới.
 */
function resolveDeadline(): number {
  const now = Date.now();
  const stored = Number(localStorage.getItem(DEADLINE_KEY));
  if (!stored || Number.isNaN(stored) || now > stored + STALE_GRACE_MS) {
    const deadline = now + COUNTDOWN_SECONDS * 1000;
    try {
      localStorage.setItem(DEADLINE_KEY, String(deadline));
    } catch {
      /* localStorage không khả dụng -> vẫn đếm ngược trong phiên hiện tại */
    }
    return deadline;
  }
  return stored;
}

function remainingSeconds(deadline: number): number {
  return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Kiểm tra server có THẬT SỰ tới được không (không tin navigator.onLine — nó chỉ
 * báo có card mạng, không báo tới được server: captive-portal, VPN rớt, server
 * 5xx đều khiến onLine=true nhưng thực chất không tải được gì).
 *
 * Fetch service-worker.js kèm cache:"no-store": file này không nằm trong precache
 * của workbox nên request đi thẳng ra mạng, phản ánh đúng khả năng kết nối thật.
 */
async function canReachServer(): Promise<boolean> {
  if (!navigator.onLine) return false; // fast-fail rẻ tiền
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    REACHABILITY_TIMEOUT_MS
  );
  try {
    const res = await fetch(`${process.env.PUBLIC_URL}/service-worker.js`, {
      cache: "no-store",
      signal: controller.signal,
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Lớp 2 — Hard recovery (chỉ gọi sau khi đã xác nhận tới được server).
 * Gỡ SW của app + xoá Cache Storage rồi reload. Thứ tự/await là bắt buộc:
 * unregister XONG rồi mới reload -> điều hướng kế tiếp không có SW điều khiển ->
 * nạp index.html + bundle mới trực tiếp từ mạng, thoát khỏi SW đang kẹt bất kể lý
 * do. Xoá cache là lưới phòng khi SW hấp hối vẫn kịp phục vụ từ precache cũ.
 *
 * (Origin dành riêng cho app này nên xoá toàn bộ cache là an toàn.)
 */
async function hardRecovery(registration: ServiceWorkerRegistration) {
  try {
    await registration.unregister();
  } catch {
    /* bỏ qua */
  }
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    /* bỏ qua */
  }
  window.location.reload(); // chỉ sau khi ĐÃ await unregister + xoá cache
}

/**
 * Modal căn giữa: đếm ngược rồi tự động cập nhật, có quy trình recovery leo thang
 * để không bao giờ treo/loop vô hạn.
 *
 * Vì API backend không tương thích ngược với client cũ, người dùng bắt buộc chạy
 * phiên bản mới nhất. Luồng:
 *  1. Đếm ngược (hoặc bấm "Cập nhật ngay") -> gửi SKIP_WAITING + nudge đánh thức SW.
 *  2. SW mới nắm quyền -> `controllerchange` (trong serviceWorkerRegistration) reload.
 *  3. Nếu 90s vẫn chưa reload (stalled) -> recovery leo thang:
 *     Lớp 1: đánh thức lại + chờ controllerchange (giữ cache, chạy cả khi offline).
 *     Lớp 2: nếu server tới được -> hard-recovery (gỡ SW + xoá cache + reload).
 *     Lớp 3: offline/không tới được server, hoặc hết lượt -> màn terminal, dừng auto.
 */
function UpdateModal({
  registration,
  onUpdate,
}: Readonly<{
  registration: ServiceWorkerRegistration;
  onUpdate: () => void;
}>) {
  const [deadline] = useState(resolveDeadline);
  const [seconds, setSeconds] = useState(() => remainingSeconds(deadline));
  // updating: đã kích hoạt cập nhật -> hiện loading, ẩn nút, dừng đếm ngược.
  const [updating, setUpdating] = useState(false);
  // stalled: chờ SW nắm quyền quá lâu -> vào quy trình recovery.
  const [stalled, setStalled] = useState(false);
  // terminal: đã bỏ cuộc tự động -> hướng dẫn người dùng đóng toàn bộ tab.
  const [terminal, setTerminal] = useState(false);
  // Lớp 0: tab này đã stalled quá nhiều lần trong phiên -> vào thẳng terminal,
  // KHÔNG chạy đếm ngược / nudge / stall nữa (chặn vòng lặp tự động).
  const [preEscalated, setPreEscalated] = useState(
    () => getUpdateAttempts() >= MAX_UPDATE_ATTEMPTS
  );

  // Chặn recovery chạy chồng nhau. Dùng ref (không phải state) để việc bật/tắt
  // không tạo lại `recover` -> không vô tình kích hoạt lại effect auto-escalate.
  const busyRef = useRef(false);

  const trigger = useCallback(() => {
    setUpdating(true);
    onUpdate();
  }, [onUpdate]);

  const recover = useCallback(() => {
    if (busyRef.current || terminal) return;
    busyRef.current = true;
    void (async () => {
      // Lớp 1 — đánh thức SW rồi chờ controllerchange. GIỮ cache, chạy được cả
      // khi offline (SKIP_WAITING + activate từ precache không cần mạng).
      try {
        await registration.update().catch(() => {});
        registration.waiting?.postMessage({ type: "SKIP_WAITING" });
      } catch {
        /* bỏ qua */
      }
      await sleep(CONTROLLERCHANGE_WAIT_MS);
      // Nếu controllerchange đã xảy ra, serviceWorkerRegistration đã reload trang
      // và đoạn code này không bao giờ chạy tới đây. Còn chạy tiếp -> chưa nắm
      // được quyền -> leo thang.

      // Lớp 2 — chỉ hard-recovery khi CÒN lượt và server THẬT SỰ tới được.
      if (getUpdateAttempts() < MAX_UPDATE_ATTEMPTS && (await canReachServer())) {
        await hardRecovery(registration); // reload -> không quay lại đây
        return;
      }

      // Lớp 3 — offline / không tới được server / hết lượt -> terminal, dừng auto.
      busyRef.current = false;
      setTerminal(true);
    })();
  }, [registration, terminal]);

  // Đếm ngược. Dừng khi đã kích hoạt / đang recovery (stalled) / terminal / preEscalated.
  useEffect(() => {
    if (preEscalated || terminal || updating || stalled) return;
    if (seconds <= 0) {
      trigger();
      return;
    }
    // Tính lại từ đồng hồ thật mỗi giây -> không lệch dù tab bị throttle.
    const timer = setTimeout(() => {
      setSeconds(remainingSeconds(deadline));
    }, 1000);
    return () => clearTimeout(timer);
  }, [seconds, deadline, updating, stalled, preEscalated, terminal, trigger]);

  // Chờ SW nắm quyền; quá STALL_TIMEOUT_MS mà chưa reload -> đánh dấu stalled và
  // TĂNG bộ đếm (đây là thất bại thật sự — không đếm ở happy-path trigger).
  useEffect(() => {
    if (!updating || terminal || stalled) return;
    const timer = setTimeout(() => {
      bumpUpdateAttempts();
      setStalled(true);
    }, STALL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [updating, terminal, stalled]);

  // Tự leo thang khi stalled — không bắt người dùng phải bấm mới thoát vòng lặp.
  useEffect(() => {
    if (stalled && !terminal) recover();
  }, [stalled, terminal, recover]);

  // Ở màn terminal: thử lại (vd người dùng vừa kết nối mạng lại). Reset để Lớp 2
  // có thể chạy, rồi kích hoạt lại recovery.
  const handleRetry = useCallback(() => {
    resetUpdateAttempts();
    busyRef.current = false;
    setPreEscalated(false);
    setTerminal(false);
    setStalled(true); // kèm terminal->false sẽ khiến effect auto-escalate chạy lại
  }, []);

  const renderBody = () => {
    if (terminal || preEscalated) {
      return (
        <>
          <div className={styles.desc}>
            Chưa thể tự hoàn tất cập nhật. Vui lòng <b>đóng tất cả tab và cửa sổ</b>{" "}
            của hệ thống rồi mở lại. Nếu vẫn còn thông báo này, kiểm tra kết nối
            mạng hoặc liên hệ quản trị hệ thống.
          </div>
          <Button
            className={styles.updateBtn}
            type="primary"
            onClick={handleRetry}
          >
            Thử lại
          </Button>
        </>
      );
    }
    if (stalled || updating) {
      return (
        <div className={styles.desc}>
          Đang cập nhật phiên bản mới, vui lòng đợi trong giây lát…
        </div>
      );
    }
    return (
      <>
        <div className={styles.desc}>
          Hệ thống sẽ tự động cập nhật sau{" "}
          <span className={styles.count}>{seconds}s</span> để đảm bảo bạn sử
          dụng phiên bản mới nhất.
        </div>
        <Button className={styles.updateBtn} type="primary" onClick={trigger}>
          Cập nhật ngay
        </Button>
      </>
    );
  };

  return (
    <Modal
      open
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={420}
      className={styles.updateModal}
    >
      <div className={styles.content}>
        <span className={styles.iconBadge}>
          <SyncOutlined spin />
        </span>
        <div className={styles.title}>Đã có phiên bản mới của hệ thống</div>
        {renderBody()}
      </div>
    </Modal>
  );
}

// Chỉ mount 1 modal cho mỗi lần load trang (onUpdate có thể được gọi nhiều lần).
let modalMounted = false;

export function showUpdateNotification(
  registration: ServiceWorkerRegistration
) {
  if (modalMounted) return;
  modalMounted = true;

  // Mount modal vào một root riêng ngoài cây app (hàm được gọi imperatively).
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);

  let triggered = false;
  const activateAndReload = () => {
    if (triggered) return;
    triggered = true;
    // Dọn deadline để đợt update sau bắt đầu đếm ngược mới.
    try {
      localStorage.removeItem(DEADLINE_KEY);
    } catch {
      /* bỏ qua */
    }
    // Yêu cầu SW mới kích hoạt. Khi nó nắm quyền, `controllerchange` (đăng ký
    // trong serviceWorkerRegistration) sẽ reload trang đúng luồng -> nạp bundle
    // mới, không cần ép reload thủ công (tránh nạp lại nhầm bundle cũ).
    registration.waiting?.postMessage({ type: "SKIP_WAITING" });

    // Tab để lâu -> SW có thể đang ngủ nên message không được xử lý ngay.
    // Chủ động đánh thức định kỳ (kể cả khi chưa có worker chờ: registration.update
    // có thể kéo về một worker mới) cho tới khi trang reload (interval tự biến mất
    // khi context bị huỷ) hoặc quá ngưỡng chờ thì dừng để quy trình recovery
    // (trong UpdateModal) tiếp quản.
    const nudge = setInterval(() => {
      registration.update().catch(() => {});
      registration.waiting?.postMessage({ type: "SKIP_WAITING" });
    }, NUDGE_INTERVAL_MS);
    setTimeout(() => clearInterval(nudge), STALL_TIMEOUT_MS);
  };

  root.render(
    <UpdateModal registration={registration} onUpdate={activateAndReload} />
  );
}